import React, { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Star, LayoutGrid, Zap, ChevronRight, ChevronDown } from "lucide-react";
import { menuItems } from "../config/menu";
import { tenantConfig } from "../config/tenantConfig";
import { useUIStore } from "../store/uiStore";
import { useAuthStore } from "../store/authStore";
import { getFilteredMenu } from "../utils/menuHelper";

interface SidebarProps {
  role: string;
}

const SIDEBAR_WIDTH = "w-64";

export const Sidebar: React.FC<SidebarProps> = ({ role }) => {
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { user } = useAuthStore();
  const activeTab = useUIStore((s) => s.activeTab);
  const setActiveTab = useUIStore((s) => s.setActiveTab);
  const favorites = useUIStore((s) => s.favorites);
  const collapsedGroups = useUIStore((s) => s.collapsedGroups);
  const toggleGroup = useUIStore((s) => s.toggleGroup);
  const toggleFavorite = useUIStore((s) => s.toggleFavorite);
  const location = useLocation();

  useEffect(() => {
    // Scroll active item into view with a slight delay to let rendering and transitions finish
    const timer = setTimeout(() => {
      // Find the active NavLink in the current view
      const activeElement = document.querySelector('.sidebar .active');
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
    
    return () => clearTimeout(timer);
  }, [location.pathname, sidebarOpen, activeTab]);

  // const [isHovered, setIsHovered] = useState(false);
  const isHovered = false; // Hover ile açılmayı iptal ettik (Kullanıcı isteğiyle deneme)
  const isExpanded = sidebarOpen; // Sadece açıkken expand et

  const menuGroups = getFilteredMenu(user, menuItems);

  const favoriteItems = menuGroups
    .map((group) => ({
      ...group,
      items: group.items.filter((item: any) => favorites.includes(item.path)),
    }))
    .filter((group) => group.items.length > 0);

  // Ortak Menü Render Fonksiyonu
  const renderList = (groups: any[], isEmptyFav: boolean = false) => {
    if (isEmptyFav && groups.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10 px-4 text-center w-full h-full shrink-0">
          <div className="bg-primary/10 p-3 rounded-full mb-3">
            <Star size={24} className="text-primary" />
          </div>
          <p className="text-xs text-gray-500">Henüz favori eklemediniz.</p>
        </div>
      );
    }

    return (
      <div className="w-full shrink-0 px-2 space-y-2">
        {groups.map((group, groupIndex) => (
          <div key={groupIndex} className="w-full">
            {group.title && (
              <h3
                onClick={() => sidebarOpen && toggleGroup(group.title)}
                className={`text-[10px] font-semibold uppercase px-4 tracking-widest transition-all duration-300 flex items-center justify-between select-none
                ${!isExpanded ? 'opacity-0 h-0 overflow-hidden mb-0 cursor-default' : `opacity-100 mb-2 text-gray-400 ${sidebarOpen ? 'cursor-pointer hover:text-primary' : 'cursor-default'}`}`}>
                <span>{group.title}</span>
                {sidebarOpen && (
                  <div className="flex items-center gap-1.5 opacity-60 hover:opacity-100 transition-opacity">
                    {collapsedGroups.includes(group.title) && (
                      <span className="bg-primary/20 text-primary px-1.5 py-0.5 rounded-md text-[9px] font-bold leading-none">
                        {group.items.length}
                      </span>
                    )}
                    {collapsedGroups.includes(group.title) ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                  </div>
                )}
              </h3>
            )}
            <div className={`flex flex-col gap-1 w-full transition-all duration-300 origin-top overflow-hidden
              ${(sidebarOpen && group.title && collapsedGroups.includes(group.title)) ? 'max-h-0 opacity-0 mb-0' : 'max-h-[1000px] opacity-100 mb-2'}`}>
              {group.items.map((item: any) => {
                const Icon = item.icon;
                const isFav = favorites.includes(item.path);
                return (
                  <div key={item.path} className={`group relative flex items-center ${isExpanded ? 'pr-2' : ''} w-full`}>
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex-1 flex items-center ${isExpanded ? 'gap-3' : 'justify-center'} p-2 rounded-lg text-sm font-medium transition-all min-w-0
                        ${isActive
                          ? `card hover:bg-primary hover:text-white ${isExpanded ? 'ml-2' : ''}`
                          : `text-gray-500 hover:bg-primary hover:text-white ${isExpanded ? 'ml-1' : ''}`
                        }`
                      }
                    >
                      <Icon className="h-5 w-5 flex-shrink-0" />
                      <span className={`truncate transition-all duration-300 ${!isExpanded ? 'opacity-0 w-0 overflow-hidden hidden' : 'opacity-100'}`}>{item.label}</span>
                    </NavLink>
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        toggleFavorite(item.path);
                      }}
                      className={`p-1.5 rounded-md transition-all flex-shrink-0 ${!isExpanded ? 'hidden' : ''} ${isFav
                        ? "text-yellow-500 opacity-100"
                        : "text-gray-300 opacity-0 group-hover:opacity-100 hover:text-yellow-400"
                        }`}
                    >
                      <Star size={14} fill={isFav ? "currentColor" : "none"} />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 bg-black/40 z-50 md:hidden transition-opacity ${sidebarOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
          }`}
        onClick={toggleSidebar}
      />

      {/* Sidebar Container */}
      <div
        className={`
          sidebar z-50 h-full fixed top-0 left-0
          transition-all duration-300 ease-in-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          md:relative md:translate-x-0 md:shrink-0
          ${sidebarOpen ? "md:w-64" : "md:w-16"}
        `}
      >
        <div
          className={`
            h-full flex flex-col overflow-hidden bg-sidebarBg shadow-xl md:shadow-none transition-all duration-300
            w-64
            ${!sidebarOpen && !isHovered ? 'md:w-16' : 'md:w-64'}
            ${!sidebarOpen && isHovered ? 'md:absolute md:left-0 md:top-0 md:bottom-0 md:z-50 md:shadow-2xl border-r border-borderColor' : 'border-r border-borderColor'}
          `}
        >
          {/* Logo Bölümü */}
          <div className="flex items-center gap-2 border-b border-borderColor p-4 flex-shrink-0">
            <img src={tenantConfig.logo} alt={tenantConfig.name} className="h-10 object-contain shrink-0" />
            <div className={`flex flex-col whitespace-nowrap transition-all duration-300 overflow-hidden ${!isExpanded ? 'opacity-0 w-0' : 'opacity-100'}`}>
              <span className="text-xl font-bold text">{tenantConfig.name}</span>
              <span className="text-xs text opacity-70 italic font-medium">{tenantConfig.slogan}</span>
            </div>
          </div>

          {/* ANIMASYONLU TAB ANAHTARI */}
          <div className={`flex-shrink-0 transition-all duration-300 overflow-hidden ${!isExpanded ? 'opacity-0 h-0 p-0' : 'opacity-100 p-3'}`}>
            <div className="relative flex card p-1 rounded-xl border border-borderColor/10 overflow-hidden">
              <div
                className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-primary rounded-lg transition-transform duration-300 ease-out z-0 shadow-md ${activeTab === "all" ? "translate-x-0" : "translate-x-[calc(100%+4px)]"
                  }`}
              />

              <button
                onClick={() => setActiveTab("all")}
                className={`relative flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold transition-colors duration-300 z-10 ${activeTab === "all" ? "text-white" : "text-gray-500"
                  }`}
              >
                <LayoutGrid size={14} />
                Menü
              </button>

              <button
                onClick={() => setActiveTab("fav")}
                className={`relative flex-1 flex items-center justify-center gap-2 py-2 text-xs font-bold transition-colors duration-300 z-10 ${activeTab === "fav" ? "text-white" : "text-gray-500"
                  }`}
              >
                <Zap size={14} fill={activeTab === "fav" ? "white" : "none"} />
                Hızlı
              </button>
            </div>
          </div>

          {/* KAYAN MENÜ ALANI */}
          <div className="flex-1 overflow-hidden relative">
            <div
              className="h-full flex transition-transform duration-500 ease-in-out"
              style={{
                transform: activeTab === "all" ? "translateX(0)" : "translateX(-50%)",
                width: "200%",
              }}
            >
              {/* Sol Taraf: Tüm Menü */}
              <div className="w-1/2 h-full overflow-y-auto overflow-x-hidden pb-10 scrollbar-thin">
                {renderList(menuGroups)}
              </div>

              {/* Sağ Taraf: Favoriler */}
              <div className="w-1/2 h-full overflow-y-auto overflow-x-hidden pb-10 scrollbar-thin">
                {renderList(favoriteItems, true)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};